import { Trash2, Image as ImageIcon, Gem, Wand2, Undo2, Scissors, ScanSearch } from "lucide-react";
import { renderImageUrl } from "@/lib/imageUrl";

/**
 * StudioGallery — right-rail grouped thumbnail grid in the user studio.
 *
 * Groups renders by `variant_group`, showing a small "N variants" label
 * when a group has multiple front-view siblings. Trash button per tile.
 * Multiview tiles (left/back/right with a `tripo_task_id`) also get a
 * quick text-only refine action — plus a one-step undo when a refine
 * snapshot is available.
 */
export default function StudioGallery({
  backendUrl,
  groupedGallery,
  totalCount,
  loading,
  currentId,
  onSelect,
  onDelete,
  onMultiviewRefine,
  onMultiviewUndo,
  onSplitComponent,
  onAutoSplit,
}) {
  return (
    <section className="col-span-12 lg:col-span-3 glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
          Your gallery
        </div>
        <span className="text-[10px] text-white/35">{totalCount}</span>
      </div>

      {loading ? (
        <div className="text-center py-10 text-white/45 text-sm">Loading…</div>
      ) : groupedGallery.length === 0 ? (
        <div className="text-center py-10">
          <ImageIcon className="w-6 h-6 text-white/15 mx-auto mb-3" />
          <p className="text-xs text-white/40">No renders yet — create your first one.</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[640px] overflow-y-auto pr-1">
          {groupedGallery.map((group, gi) => {
            const fronts = group.filter((g) => (g.view || "front") === "front");
            return (
              <div key={group[0].variant_group || group[0].id} data-testid={`studio-group-${gi}`}>
                {fronts.length > 1 && (
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1.5">
                    {fronts.length} variants
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {group.map((g) => {
                    const isBareMetal = g.render_kind === "cad_ready";
                    const canQuickRefine = !!g.tripo_task_id && !!onMultiviewRefine;
                    return (
                    <button
                      key={g.id}
                      onClick={() => onSelect(g)}
                      data-testid={`studio-gallery-${g.id}`}
                      className={`group relative aspect-square rounded-lg overflow-hidden border transition ${
                        currentId === g.id
                          ? "border-[#D4AF37]/60 ring-1 ring-[#D4AF37]/40"
                          : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img
                        src={renderImageUrl(backendUrl, g)}
                        alt={g.prompt}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      {isBareMetal && (
                        <span
                          data-testid={`studio-gallery-${g.id}-bare-metal-badge`}
                          title="Bare metal · pre-polish stage (no stones / enamel / plating)"
                          className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/70 border border-[#D4AF37]/45 text-[#D4AF37] text-[8.5px] uppercase tracking-[0.16em] font-medium leading-none"
                        >
                          <Gem className="w-2.5 h-2.5" /> Bare metal
                        </span>
                      )}
                      <span className="absolute bottom-1.5 left-1.5 text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]">
                        {g.view && g.view !== "front" ? g.view : (g.style || "Custom")}
                      </span>
                      <span className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                        {g.can_undo_refine && onMultiviewUndo && (
                          <span
                            onClick={(e) => { e.stopPropagation(); onMultiviewUndo(g); }}
                            data-testid={`studio-gallery-${g.id}-undo`}
                            title="Undo last refine (free)"
                            className="w-6 h-6 rounded-full bg-black/65 border border-white/20 text-white/85 hover:bg-white/10 flex items-center justify-center"
                          >
                            <Undo2 className="w-3 h-3" />
                          </span>
                        )}
                        {canQuickRefine && (
                          <span
                            onClick={(e) => { e.stopPropagation(); onMultiviewRefine(g); }}
                            data-testid={`studio-gallery-${g.id}-refine`}
                            title="Quick refine this view (text prompt, 1 credit)"
                            className="w-6 h-6 rounded-full bg-black/65 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20 flex items-center justify-center"
                          >
                            <Wand2 className="w-3 h-3" />
                          </span>
                        )}
                        {onSplitComponent && (
                          <span
                            onClick={(e) => { e.stopPropagation(); onSplitComponent(g); }}
                            data-testid={`studio-gallery-${g.id}-split`}
                            title="Split a component onto white (1 credit)"
                            className="w-6 h-6 rounded-full bg-black/65 border border-white/20 text-white/85 hover:bg-white/10 flex items-center justify-center"
                          >
                            <Scissors className="w-3 h-3" />
                          </span>
                        )}
                        {onAutoSplit && (
                          <span
                            onClick={(e) => { e.stopPropagation(); onAutoSplit(g); }}
                            data-testid={`studio-gallery-${g.id}-auto-split`}
                            title="Auto-split all components (1 credit detect + 1 per extract)"
                            className="w-6 h-6 rounded-full bg-black/65 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20 flex items-center justify-center"
                          >
                            <ScanSearch className="w-3 h-3" />
                          </span>
                        )}
                        <span
                          onClick={(e) => { e.stopPropagation(); onDelete(g); }}
                          className="w-6 h-6 rounded-full bg-black/60 border border-white/10 text-white/65 hover:text-red-400 flex items-center justify-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </span>
                      </span>
                    </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
